import React, { useState, useEffect } from 'react';
import { processesApi } from '../api';
import { toast } from 'sonner';
import {
  GitBranch,
  Plus,
  Search,
  Edit,
  Trash2,
  X,
  Loader2,
  CheckCircle,
  XCircle,
} from 'lucide-react';

const Processes = () => {
  const [processes, setProcesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingProcess, setEditingProcess] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true,
  });

  useEffect(() => {
    loadProcesses();
  }, []);

  const loadProcesses = async () => {
    try {
      setLoading(true);
      const response = await processesApi.list();
      setProcesses(response.data || []);
    } catch (error) {
      toast.error('Erro ao carregar processos');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (process = null) => {
    if (process) {
      setEditingProcess(process);
      setFormData({
        name: process.name || '',
        description: process.description || '',
        is_active: process.is_active !== false,
      });
    } else {
      setEditingProcess(null);
      setFormData({
        name: '',
        description: '',
        is_active: true,
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingProcess(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name) {
      toast.error('O nome do processo é obrigatório');
      return;
    }

    setSaving(true);

    const cleanedData = {
      ...formData,
      description: formData.description || null,
    };

    try {
      if (editingProcess) {
        await processesApi.update(editingProcess.id, cleanedData);
        toast.success('Processo atualizado com sucesso!');
      } else {
        await processesApi.create(cleanedData);
        toast.success('Processo cadastrado com sucesso!');
      }

      handleCloseModal();
      loadProcesses();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errorMsg = Array.isArray(detail)
        ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ')
        : (detail || 'Erro ao salvar processo');
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (process) => {
    if (!window.confirm(`Desativar o processo "${process.name}"?`)) return;

    try {
      await processesApi.delete(process.id);
      toast.success('Processo desativado com sucesso!');
      loadProcesses();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Erro ao desativar processo');
    }
  };

  const filteredProcesses = processes.filter(p =>
    p.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const activeProcesses = filteredProcesses.filter(p => p.is_active);
  const inactiveProcesses = filteredProcesses.filter(p => !p.is_active);

  return (
    <div className="space-y-6" data-testid="processes-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <GitBranch className="text-green-400" />
            Cadastro de Processos
          </h1>
          <p className="text-gray-400 mt-1">Gerencie os processos utilizados no sistema</p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="btn-primary"
        >
          <Plus size={18} />
          Novo Processo
        </button>
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
          <input
            type="text"
            placeholder="Buscar por nome..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input w-full pl-10"
            data-testid="search-input"
          />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <GitBranch className="text-blue-400" size={24} />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Total de Processos</p>
              <p className="text-2xl font-bold text-blue-400">{processes.length}</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <CheckCircle className="text-green-400" size={24} />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Ativos</p>
              <p className="text-2xl font-bold text-green-400">
                {activeProcesses.length}
              </p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-500/20 rounded-lg">
              <XCircle className="text-red-400" size={24} />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Inativos</p>
              <p className="text-2xl font-bold text-red-400">
                {inactiveProcesses.length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-green-500" size={32} />
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-800">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Nome</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Descrição</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {filteredProcesses.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                    Nenhum processo encontrado
                  </td>
                </tr>
              ) : (
                filteredProcesses.map(p => (
                  <tr key={p.id} className="hover:bg-gray-800/50">
                    <td className="px-4 py-3">
                      <span className="text-white">{p.name}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-400 max-w-xs truncate">
                      {p.description || '-'}
                    </td>
                    <td className="px-4 py-3">
                      {p.is_active ? (
                        <span className="px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-400 flex items-center gap-1 w-fit">
                          <CheckCircle size={12} />
                          Ativo
                        </span>
                      ) : (
                        <span className="px-2 py-1 rounded-full text-xs bg-red-500/20 text-red-400 flex items-center gap-1 w-fit">
                          <XCircle size={12} />
                          Inativo
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleOpenModal(p)}
                          className="p-2 text-gray-400 hover:text-blue-400 hover:bg-gray-700 rounded-lg"
                          title="Editar"
                        >
                          <Edit size={18} />
                        </button>
                        {p.is_active ? (
                          <button
                            onClick={() => handleDelete(p)}
                            className="p-2 text-gray-400 hover:text-red-400 hover:bg-gray-700 rounded-lg"
                            title="Desativar"
                          >
                            <Trash2 size={18} />
                          </button>
                        ) : (
                          <button
                            onClick={() => {
                              if (!window.confirm(`Reativar o processo "${p.name}"?`)) return;
                              processesApi.update(p.id, { is_active: true }).then(() => {
                                toast.success('Processo reativado com sucesso!');
                                loadProcesses();
                              }).catch(() => {
                                toast.error('Erro ao reativar processo');
                              });
                            }}
                            className="p-2 text-gray-400 hover:text-green-400 hover:bg-gray-700 rounded-lg"
                            title="Reativar"
                          >
                            <CheckCircle size={18} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Form Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h2 className="text-lg font-semibold text-white">
                {editingProcess ? 'Editar Processo' : 'Novo Processo'}
              </h2>
              <button onClick={handleCloseModal} className="text-gray-400 hover:text-white">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Nome <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Produção"
                  className="input w-full"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Descrição</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descrição do processo..."
                  className="input w-full"
                  rows={2}
                />
              </div>

              <div className="flex items-center">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-600 bg-gray-700 text-green-500"
                  />
                  <span className="text-sm font-medium text-gray-300">Processo Ativo</span>
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <button type="button" onClick={handleCloseModal} className="btn-secondary" disabled={saving}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary flex items-center gap-2" disabled={saving}>
                  {saving ? <><Loader2 size={18} className="animate-spin" /> Salvando...</> : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Processes;
